import React, { useEffect, useRef } from 'react'

// Пять вариантов хэллоуинского тумана. Все — только код: CSS-градиенты,
// inline-SVG фильтры, Canvas 2D и WebGL-шейдер, без файлов картинок.

const prefersReducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

// Цикл анимации для canvas: ограничение FPS, пауза при скрытой вкладке,
// при reduced-motion рисуется один статичный кадр.
const useCanvasLoop = (canvasRef, { scale, fps, setup }) => {
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return undefined
    const frame = setup(canvas)
    if (!frame) return undefined

    const resize = () => {
      canvas.width = Math.max(1, Math.round(window.innerWidth * scale))
      canvas.height = Math.max(1, Math.round(window.innerHeight * scale))
      frame.resize?.(canvas.width, canvas.height)
    }
    resize()
    window.addEventListener('resize', resize)

    const start = performance.now()
    if (prefersReducedMotion()) {
      frame.draw(8, 0)
      return () => {
        window.removeEventListener('resize', resize)
        frame.dispose?.()
      }
    }

    let raf = 0
    let last = 0
    const interval = 1000 / fps
    const tick = (now) => {
      raf = requestAnimationFrame(tick)
      if (document.hidden || now - last < interval) return
      const dt = last ? Math.min((now - last) / 1000, 0.1) : 0
      last = now
      frame.draw((now - start) / 1000, dt)
    }
    raf = requestAnimationFrame(tick)

    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', resize)
      frame.dispose?.()
    }
  }, [canvasRef, scale, fps, setup])
}

// Статичный горизонт кладбища: холмы, надгробия, кресты и голое дерево
const Graveyard = () => (
  <svg className="hw-graveyard" viewBox="0 0 1200 300" preserveAspectRatio="xMidYMax slice" aria-hidden="true">
    <path d="M0 210 C 180 150 320 175 470 200 S 760 150 900 180 S 1110 170 1200 160 L1200 300 L0 300 Z" fill="#0b0912" />
    <g fill="#07060c">
      <path d="M120 230 v-38 a22 22 0 0 1 44 0 v38 z" />
      <path d="M205 236 v-26 a15 15 0 0 1 30 0 v26 z" />
      <rect x="300" y="168" width="7" height="60" />
      <rect x="286" y="184" width="35" height="7" />
      <path d="M720 214 v-44 a26 26 0 0 1 52 0 v44 z" />
      <rect x="830" y="150" width="8" height="70" />
      <rect x="814" y="168" width="40" height="8" />
      <path d="M960 206 v-30 a17 17 0 0 1 34 0 v30 z" />
      <path d="M0 300 L0 235 C 220 205 420 245 640 238 S 1000 215 1200 232 L1200 300 Z" />
    </g>
    <path
      d="M560 236 C 556 190 552 150 560 104 M560 150 C 530 132 512 110 500 82 M512 108 C 498 104 486 96 478 86 M558 128 C 586 108 604 86 612 56 M602 78 C 618 72 632 62 640 50 M560 104 C 548 84 546 66 552 44"
      stroke="#07060c"
      strokeWidth="7"
      strokeLinecap="round"
      fill="none"
    />
  </svg>
)

// 1. «Шёлк» — SVG feTurbulence: настоящий фрактальный шум, два слоя дрейфуют навстречу.
// stitchTiles делает края шума совпадающими, поэтому две одинаковые копии подряд
// (отдельные svg с общей системой координат) дают бесконечную ленту без шва.
const SilkLayer = ({ id, freq, seed, color, className }) => (
  <div className={`hw-silk ${className || ''}`}>
    <svg aria-hidden="true">
      <filter id={id} x="0" y="0" width="100%" height="100%">
        <feTurbulence type="fractalNoise" baseFrequency={freq} numOctaves="4" seed={seed} stitchTiles="stitch" />
        <feColorMatrix type="matrix" values={`0 0 0 0 ${color[0]}  0 0 0 0 ${color[1]}  0 0 0 0 ${color[2]}  1.9 0 0 0 -0.72`} />
      </filter>
      <rect width="100%" height="100%" filter={`url(#${id})`} />
    </svg>
    <svg aria-hidden="true">
      <rect width="100%" height="100%" filter={`url(#${id})`} />
    </svg>
  </div>
)

export const SilkFog = () => (
  <div className="hw-fog" aria-hidden="true">
    <div className="hw-base" />
    <SilkLayer id="hw-silk-a" freq="0.0035 0.009" seed="7" color={[0.62, 0.55, 0.85]} />
    <SilkLayer id="hw-silk-b" freq="0.006 0.016" seed="21" color={[0.35, 0.95, 0.55]} className="hw-silk--b" />
    <div className="hw-vignette" />
  </div>
)

// 2. «Низина» — только CSS-градиенты: три слоя тумана у земли с параллаксом,
// луна и силуэт кладбища. Самый дешёвый вариант для слабых телефонов.
export const LowlandFog = () => (
  <div className="hw-fog" aria-hidden="true">
    <div className="hw-base" />
    <div className="hw-moon" />
    <div className="hw-low-band hw-low-band--back" />
    <Graveyard />
    <div className="hw-low-band hw-low-band--mid" />
    <div className="hw-low-band hw-low-band--front" />
    <div className="hw-vignette" />
  </div>
)

// 3. «Клубы» — Canvas 2D: процедурные клубы дыма, которые разгоняются пальцем/курсором.
// Холст рисуется в 35% разрешения и растягивается — получается мягкое размытие бесплатно.
const PUFF_COLORS = [[170, 150, 220], [120, 255, 160], [255, 140, 60]]

const createPuffSprite = ([r, g, b]) => {
  const size = 256
  const sprite = document.createElement('canvas')
  sprite.width = size
  sprite.height = size
  const ctx = sprite.getContext('2d')
  // Несколько смещённых кругов дают рваный край клуба
  for (let i = 0; i < 7; i++) {
    const x = size / 2 + Math.cos(i * 2.4) * size * 0.16
    const y = size / 2 + Math.sin(i * 2.4) * size * 0.1
    const radius = size * (0.22 + (i % 3) * 0.06)
    const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius)
    gradient.addColorStop(0, `rgba(${r},${g},${b},0.22)`)
    gradient.addColorStop(1, `rgba(${r},${g},${b},0)`)
    ctx.fillStyle = gradient
    ctx.beginPath()
    ctx.arc(x, y, radius, 0, Math.PI * 2)
    ctx.fill()
  }
  return sprite
}

const setupPuffs = (canvas) => {
  const ctx = canvas.getContext('2d')
  const sprites = PUFF_COLORS.map(createPuffSprite)
  const pointer = { x: -1e4, y: -1e4 }
  let width = 1
  let height = 1
  let puffs = []

  const onMove = (event) => {
    const rect = canvas.getBoundingClientRect()
    pointer.x = ((event.clientX - rect.left) / rect.width) * width
    pointer.y = ((event.clientY - rect.top) / rect.height) * height
  }
  window.addEventListener('pointermove', onMove, { passive: true })

  return {
    resize: (w, h) => {
      width = w
      height = h
      puffs = Array.from({ length: 34 }, (_, i) => {
        // Фиолетовые клубы основные, зелёные — реже, оранжевые — единичные
        const tone = i % 9 === 0 ? 2 : i % 4 === 0 ? 1 : 0
        return {
          x: Math.random() * w,
          y: h * (0.35 + Math.random() * 0.75),
          size: h * (0.35 + Math.random() * 0.45),
          vx: (Math.random() * 0.5 + 0.5) * (i % 2 ? 1 : -1) * w * 0.02,
          vy: 0,
          spin: Math.random() * Math.PI,
          tone
        }
      })
    },
    draw: (time, dt) => {
      ctx.clearRect(0, 0, width, height)
      const pushRadius = height * 0.3
      for (const puff of puffs) {
        const dx = puff.x - pointer.x
        const dy = puff.y - pointer.y
        const distance = Math.hypot(dx, dy)
        if (distance < pushRadius && distance > 0) {
          const force = (1 - distance / pushRadius) * width * 0.6
          puff.vx += (dx / distance) * force * dt
          puff.vy += (dy / distance) * force * dt
        }
        // Затухание импульса от касания и возврат к спокойному дрейфу
        puff.vy *= 0.96
        puff.vx += (Math.sign(puff.vx || 1) * width * 0.015 - puff.vx) * 0.02
        puff.x += puff.vx * dt
        puff.y += (puff.vy + Math.sin(time * 0.3 + puff.spin) * height * 0.03) * dt
        const half = puff.size / 2
        if (puff.x < -half) puff.x = width + half
        if (puff.x > width + half) puff.x = -half
        puff.y = Math.min(Math.max(puff.y, height * 0.2), height * 1.1)

        ctx.save()
        ctx.translate(puff.x, puff.y)
        ctx.rotate(puff.spin + time * 0.02)
        ctx.drawImage(sprites[puff.tone], -half, -half, puff.size, puff.size)
        ctx.restore()
      }
    },
    dispose: () => window.removeEventListener('pointermove', onMove)
  }
}

export const PuffFog = () => {
  const canvasRef = useRef(null)
  useCanvasLoop(canvasRef, { scale: 0.35, fps: 40, setup: setupPuffs })
  return (
    <div className="hw-fog" aria-hidden="true">
      <div className="hw-base" />
      <canvas ref={canvasRef} className="hw-canvas" />
      <div className="hw-vignette" />
    </div>
  )
}

// 4. «Котёл» — WebGL-шейдер: fbm-шум с domain warping, ядовито-зелёный пар
// снизу и фиолетовая мгла сверху. Самый «дорогой» вид, рендер в 40% разрешения.
const VERTEX_SHADER = 'attribute vec2 p;void main(){gl_Position=vec4(p,0.,1.);}'
const FRAGMENT_SHADER = `
precision mediump float;
uniform vec2 r;
uniform float t;
float h(vec2 p){return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453);}
float n(vec2 p){vec2 i=floor(p),f=fract(p);f=f*f*(3.-2.*f);
  return mix(mix(h(i),h(i+vec2(1.,0.)),f.x),mix(h(i+vec2(0.,1.)),h(i+vec2(1.,1.)),f.x),f.y);}
float fbm(vec2 p){float v=0.,a=.5;mat2 m=mat2(1.6,1.2,-1.2,1.6);
  for(int i=0;i<5;i++){v+=a*n(p);p=m*p;a*=.5;}return v;}
void main(){
  vec2 uv=gl_FragCoord.xy/r;
  vec2 p=uv*vec2(r.x/r.y,1.)*2.2;
  vec2 q=vec2(fbm(p+vec2(0.,t*.06)),fbm(p+vec2(5.2,1.3)-t*.04));
  vec2 w=vec2(fbm(p+2.*q+vec2(1.7,9.2)+t*.05),fbm(p+2.*q+vec2(8.3,2.8)));
  float f=fbm(p+2.4*w+vec2(t*.03,-t*.08));
  float ground=smoothstep(.85,0.,uv.y);
  vec3 col=mix(vec3(.018,.012,.035),vec3(.05,.02,.075),uv.y);
  col+=vec3(.42,.24,.62)*f*f*(.25+.75*uv.y)*.85;
  col+=vec3(.2,1.,.45)*pow(f,3.2)*ground*1.1;
  col+=vec3(1.,.45,.08)*pow(max(w.x-.45,0.),2.)*ground*.6;
  float vig=smoothstep(1.25,.35,length(uv-vec2(.5,.45)));
  gl_FragColor=vec4(col*vig,1.);
}`

const setupCauldron = (canvas) => {
  const gl = canvas.getContext('webgl', { antialias: false, alpha: false, powerPreference: 'low-power' })
  if (!gl) return null
  const compile = (type, source) => {
    const shader = gl.createShader(type)
    gl.shaderSource(shader, source)
    gl.compileShader(shader)
    return shader
  }
  const program = gl.createProgram()
  gl.attachShader(program, compile(gl.VERTEX_SHADER, VERTEX_SHADER))
  gl.attachShader(program, compile(gl.FRAGMENT_SHADER, FRAGMENT_SHADER))
  gl.linkProgram(program)
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return null
  gl.useProgram(program)

  const buffer = gl.createBuffer()
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer)
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW)
  const position = gl.getAttribLocation(program, 'p')
  gl.enableVertexAttribArray(position)
  gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0)
  const resolution = gl.getUniformLocation(program, 'r')
  const time = gl.getUniformLocation(program, 't')

  return {
    resize: (w, h) => {
      gl.viewport(0, 0, w, h)
      gl.uniform2f(resolution, w, h)
    },
    draw: (seconds) => {
      gl.uniform1f(time, seconds)
      gl.drawArrays(gl.TRIANGLES, 0, 3)
    },
    dispose: () => {
      gl.deleteBuffer(buffer)
      gl.deleteProgram(program)
      // Браузер держит ограниченное число WebGL-контекстов — отдаём сразу
      gl.getExtension('WEBGL_lose_context')?.loseContext()
    }
  }
}

export const CauldronFog = () => {
  const canvasRef = useRef(null)
  useCanvasLoop(canvasRef, { scale: 0.4, fps: 30, setup: setupCauldron })
  return (
    <div className="hw-fog" aria-hidden="true">
      {/* Фон на случай отсутствия WebGL */}
      <div className="hw-base" />
      <canvas ref={canvasRef} className="hw-canvas" />
    </div>
  )
}

// 5. «Лунная дымка» — CSS: вращающиеся лучи от луны, мерцающие звёзды
// и тонкие полосы тумана поперёк экрана.
const STARS = Array.from({ length: 46 }, (_, i) => {
  const x = (i * 37.7) % 100
  const y = (i * 23.3) % 58
  const size = i % 7 === 0 ? 2 : 1
  return `radial-gradient(${size}px ${size}px at ${x.toFixed(1)}% ${y.toFixed(1)}%, rgba(255,255,255,${i % 5 === 0 ? 0.9 : 0.5}), transparent)`
})
const STARS_A = STARS.filter((_, i) => i % 2).join(',')
const STARS_B = STARS.filter((_, i) => !(i % 2)).join(',')

const WISPS = [
  { top: '38%', h: '14vh', a: 0.08, speed: '46s' },
  { top: '55%', h: '20vh', a: 0.11, speed: '34s' },
  { top: '70%', h: '18vh', a: 0.14, speed: '52s' },
  { top: '82%', h: '22vh', a: 0.18, speed: '28s' }
]

export const MoonlitFog = () => (
  <div className="hw-fog" aria-hidden="true">
    <div className="hw-base" />
    <div className="hw-stars" style={{ backgroundImage: STARS_A }} />
    <div className="hw-stars hw-stars--twinkle" style={{ backgroundImage: STARS_B }} />
    <div className="hw-rays" />
    <div className="hw-moon" />
    {WISPS.map((wisp, index) => (
      <div
        key={index}
        className="hw-wisp"
        style={{ '--hw-top': wisp.top, '--hw-h': wisp.h, '--hw-a': wisp.a, '--hw-speed': wisp.speed, animationDelay: `-${index * 7}s` }}
      />
    ))}
    <div className="hw-horizon" />
    <div className="hw-vignette" />
  </div>
)

export const FOGS = [
  { id: 'silk', name: 'Шёлк', tech: 'SVG feTurbulence', cost: 'средне', Component: SilkFog },
  { id: 'lowland', name: 'Низина', tech: 'CSS-градиенты', cost: 'дёшево', Component: LowlandFog },
  { id: 'puffs', name: 'Клубы', tech: 'Canvas 2D, реагирует на касание', cost: 'средне', Component: PuffFog },
  { id: 'cauldron', name: 'Котёл', tech: 'WebGL-шейдер', cost: 'дорого', Component: CauldronFog },
  { id: 'moonlit', name: 'Лунная дымка', tech: 'CSS: лучи, звёзды, полосы', cost: 'дёшево', Component: MoonlitFog }
]
